import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { knowledgeBaseService } from '../services/knowledgeBaseService';
import type { KBDocumentDetails } from '../services/knowledgeBaseService';
import { solutionService } from '../services/solutionService';
import MarkdownViewer from './MarkdownViewer';
import DerivativesDropdown from './DerivativesDropdown';
import { useDocumentDerivatives } from '../hooks/useDocumentDerivatives';

interface KBDocumentsPaneProps {
  kbId: string;
  kbName: string;
  subProjectId: string;
  solutionId?: string | null;
  /** Relevance context of the workspace (document_relevance context_field). */
  relevanceContext: string;
  /** Selectable KBs; when more than one, a selector is shown. */
  kbOptions?: { id: string; name: string }[];
  onKbChange?: (kbId: string) => void;
  isFtoField: boolean;
  ftoReloadTrigger: number;
  onFtoPatentsChanged: () => void;
  /** SOTA only: launch Full KB Review from the Documents tab header. */
  onLaunchFullKBReview?: () => void;
  /** SOTA only: launch FUF (Field Update Flow) from the Documents tab header. */
  onLaunchFuf?: () => void;
}

const KBDocumentsPane: React.FC<KBDocumentsPaneProps> = ({
  kbId, kbName: _kbName, subProjectId, solutionId, relevanceContext, kbOptions, onKbChange,
  isFtoField, ftoReloadTrigger, onFtoPatentsChanged,
  onLaunchFullKBReview, onLaunchFuf,
}) => {
  const { t } = useTranslation();
  const [docs, setDocs] = useState<KBDocumentDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [rescoring, setRescoring] = useState(false);
  const [rescoreError, setRescoreError] = useState<string | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const saveTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    derivatives, availableFunctions, processingDoc, markdownViewer, setMarkdownViewer,
    loadDocumentDerivatives, hasDerivative, executeFunction, fetchJobs, cancelFetch,
  } = useDocumentDerivatives(kbId);

  const loadDocs = useCallback(() => {
    setLoading(true);
    knowledgeBaseService.getDocumentsWithDetails(kbId, 0, 200, undefined, relevanceContext, solutionId).then(r => {
      setDocs(r.documents);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [kbId, relevanceContext, solutionId]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  useEffect(() => {
    if (!isFtoField || !solutionId) return;
    solutionService.get(subProjectId, solutionId).then(sol => {
      setCheckedIds(new Set(sol.fto_initial_patents || []));
    }).catch(() => {});
  }, [isFtoField, solutionId, subProjectId, ftoReloadTrigger]);

  const handleRescore = async () => {
    setRescoring(true);
    setRescoreError(null);
    try {
      await knowledgeBaseService.rescore(kbId, subProjectId, relevanceContext, solutionId);
      loadDocs();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setRescoreError(detail || t('workspace.docs.rescoreFailed', 'Re-score failed'));
    } finally {
      setRescoring(false);
    }
  };

  const handleCheck = (docId: string, checked: boolean) => {
    const next = new Set(checkedIds);
    if (checked) next.add(docId); else next.delete(docId);
    setCheckedIds(next);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      if (!solutionId) return;
      try {
        await solutionService.patchFtoInitialPatents(subProjectId, solutionId, [...next]);
        onFtoPatentsChanged();
      } catch { /* ignore */ }
    }, 1000);
  };

  if (loading) return <div className="p-3 text-xs text-slate-400">{t('common.loading', 'Loading...')}</div>;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {markdownViewer && (
        <MarkdownViewer
          title={markdownViewer.title}
          content={markdownViewer.content}
          onClose={() => setMarkdownViewer(null)}
        />
      )}
      <div className="flex items-center gap-1 px-2 py-1 border-b border-slate-200 bg-slate-50 flex-shrink-0">
        {kbOptions && kbOptions.length > 1 && onKbChange && (
          <select
            value={kbId}
            onChange={e => onKbChange(e.target.value)}
            className="text-[10px] border border-slate-300 rounded px-1 py-0.5 bg-white text-slate-600 max-w-[200px] flex-shrink"
            title={t('workspace.docs.kbSelector', 'Knowledge Base shown')}
          >
            {kbOptions.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.name}</option>
            ))}
          </select>
        )}
        {onLaunchFuf && (
          <button onClick={onLaunchFuf}
            className="text-[10px] px-2 py-0.5 rounded border border-emerald-400 text-emerald-700 hover:bg-emerald-50 font-semibold flex-shrink-0"
            title={t('fieldUpdate.buttonTitle', 'Update this field from a Knowledge Base')}>
            {t('fieldUpdate.buttonLabel', '↑ KB')}
          </button>
        )}
        {onLaunchFullKBReview && (
          <button onClick={onLaunchFullKBReview}
            className="text-[10px] px-2 py-0.5 rounded border border-violet-400 text-violet-600 hover:bg-violet-50 font-semibold flex-shrink-0">
            {t('fullKbReview.buttonLabel', 'Full Review')}
          </button>
        )}
        <div className="flex-1" />
        <button
          onClick={handleRescore}
          disabled={rescoring}
          className="text-[10px] px-2 py-0.5 rounded border border-slate-300 text-slate-500 hover:border-blue-400 hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {rescoring ? t('workspace.docs.rescoring', 'Scoring…') : t('workspace.docs.rescore', 'Re-score')}
        </button>
      </div>
      {rescoreError && (
        <div className="px-2 py-1 text-[10px] text-red-600 bg-red-50 border-b border-red-200 flex-shrink-0">
          {rescoreError}
        </div>
      )}
      <div className="overflow-auto flex-1">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
            <tr>
              {isFtoField && <th className="px-2 py-1 text-left font-semibold text-slate-500 w-8">FTO</th>}
              <th className="px-2 py-1 text-left font-semibold text-slate-500 w-[17ch]">{t('workspace.docs.title', 'Title')}</th>
              <th className="px-2 py-1 text-left font-semibold text-slate-500 w-10">{t('workspace.docs.relevance', 'Rel.')}</th>
              <th className="px-2 py-1 text-left font-semibold text-slate-500">{t('workspace.docs.justification', 'Justification')}</th>
              <th className="px-2 py-1 text-left font-semibold text-slate-500 w-24">{t('workspace.docs.derivatives', 'Dérivés')}</th>
            </tr>
          </thead>
          <tbody>
            {docs.map(doc => (
              <tr key={doc.id} className="border-b border-slate-100 hover:bg-slate-50">
                {isFtoField && (
                  <td className="px-2 py-1 text-center">
                    <input
                      type="checkbox"
                      checked={checkedIds.has(doc.id)}
                      onChange={e => handleCheck(doc.id, e.target.checked)}
                      className="cursor-pointer"
                    />
                  </td>
                )}
                <td className="px-2 py-1 text-slate-700 w-[17ch] max-w-[17ch] truncate" title={doc.display_name}>{doc.display_name}</td>
                <td
                  className={`px-2 py-1 w-10 text-center ${doc.stale ? 'text-slate-300 italic' : 'text-slate-500'}`}
                  title={doc.stale ? t('workspace.docs.staleTooltip', 'Assessed against an earlier version of this field — re-score to refresh') : undefined}
                >
                  {typeof doc.relevance === 'number' ? Math.round(doc.relevance > 1 ? doc.relevance : doc.relevance * 100) : (doc.relevance ?? '—')}
                </td>
                <td className="px-2 py-1 text-slate-500 truncate max-w-xs" title={doc.justification ?? ''}>{doc.justification ?? '—'}</td>
                <td className="px-2 py-1">
                  <DerivativesDropdown
                    doc={doc}
                    functions={availableFunctions}
                    isProcessing={processingDoc === doc.id}
                    hasDerivative={hasDerivative}
                    onExecute={executeFunction}
                    placeholder={t('workspace.docs.derivativesPlaceholder', 'Ouvrir…')}
                    className="w-full text-[10px] border border-slate-300 rounded px-1 py-0.5 bg-white text-slate-600 cursor-pointer hover:border-blue-400 disabled:opacity-40"
                    onOpen={(id) => { if (!derivatives[id]) loadDocumentDerivatives(id); }}
                    fetchJob={fetchJobs[doc.id] ?? null}
                    onCancelFetch={() => cancelFetch(doc.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {docs.length === 0 && (
          <div className="p-4 text-xs text-slate-400 text-center">{t('workspace.docs.empty', 'No documents in this knowledge base.')}</div>
        )}
      </div>
    </div>
  );
};

export default KBDocumentsPane;
